import { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Mic2, Music, Calendar, Instagram, Twitter, Globe, Headphones,
  ChevronRight, ArrowLeft, Radio, Star, ExternalLink, Camera, X, ChevronLeft
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const genreColors = {
  "Blues": "bg-blue-500/20 text-blue-400 border-blue-500/30",
  "R&B": "bg-purple-500/20 text-purple-400 border-purple-500/30",
  "Jazz": "bg-amber-500/20 text-amber-400 border-amber-500/30",
  "Soul": "bg-pink-500/20 text-pink-400 border-pink-500/30",
  "Gospel": "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
  "Rock": "bg-red-500/20 text-red-400 border-red-500/30",
  "Hip-Hop": "bg-green-500/20 text-green-400 border-green-500/30",
  "Techno": "bg-cyan-500/20 text-cyan-400 border-cyan-500/30",
  "Neo Soul": "bg-indigo-500/20 text-indigo-400 border-indigo-500/30",
  "Electronic": "bg-teal-500/20 text-teal-400 border-teal-500/30",
};

const defaultGenreColor = "bg-white/10 text-chrome-mid border-white/20";

const SocialIcon = ({ type }) => {
  switch(type) {
    case "instagram": return <Instagram className="w-4 h-4" />;
    case "twitter": return <Twitter className="w-4 h-4" />;
    case "facebook": return <Globe className="w-4 h-4" />;
    case "soundcloud": return <Headphones className="w-4 h-4" />;
    case "website": return <Globe className="w-4 h-4" />;
    default: return <ExternalLink className="w-4 h-4" />;
  }
};

// DJ Card Component
const DJCard = ({ dj, featured = false }) => {
  const photoUrl = dj.photo_url 
    ? (dj.photo_url.startsWith("/api/") ? `${BACKEND_URL}${dj.photo_url}` : dj.photo_url) 
    : null;

  if (featured) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <Link to={`/djs/${dj.id}`} className="block group" data-testid={`featured-dj-${dj.id}`}>
          <div className="bg-surface/80 backdrop-blur-md border border-neon-red/30 overflow-hidden hover:border-neon-red/60 transition-all">
            <div className="flex flex-col md:flex-row">
              {/* Photo */}
              <div className="md:w-1/3 relative overflow-hidden">
                {photoUrl ? (
                  <img src={photoUrl} alt={dj.name} className="w-full h-64 md:h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                ) : (
                  <div className="w-full h-64 md:h-full bg-gradient-to-br from-neon-red/20 to-black flex items-center justify-center">
                    <Mic2 className="w-20 h-20 text-neon-red/40" />
                  </div>
                )}
                <div className="absolute top-4 left-4 bg-neon-red px-3 py-1 flex items-center gap-1.5">
                  <Star className="w-3 h-3 fill-white" />
                  <span className="font-mono text-xs tracking-wider text-white">FEATURED DJ</span>
                </div>
              </div>

              {/* Info */}
              <div className="md:w-2/3 p-6 md:p-8 flex flex-col justify-center">
                <p className="font-mono text-xs tracking-[0.3em] text-neon-red mb-2">{dj.show_name || "ON AIR"}</p>
                <h2 className="font-display text-3xl md:text-4xl chrome-text mb-1">{dj.stage_name || dj.name}</h2>
                {dj.stage_name && dj.stage_name !== dj.name && (
                  <p className="text-chrome-dark text-sm mb-3">{dj.name}</p>
                )}
                <p className="text-chrome-mid leading-relaxed mb-4 line-clamp-3">{dj.bio}</p>
                
                {dj.show_schedule && (
                  <div className="flex items-center gap-2 text-chrome-mid text-sm mb-4">
                    <Calendar className="w-4 h-4 text-neon-red" />
                    <span>{dj.show_schedule}</span>
                  </div>
                )}

                <div className="flex flex-wrap gap-2 mb-4">
                  {(dj.genres || []).map(g => (
                    <span key={g} className={`text-xs px-2.5 py-1 border ${genreColors[g] || defaultGenreColor}`}>{g}</span>
                  ))}
                </div>

                <div className="flex items-center gap-2 text-neon-red font-mono text-sm group-hover:gap-3 transition-all">
                  VIEW FULL PROFILE <ChevronRight className="w-4 h-4" />
                </div>
              </div>
            </div>
          </div>
        </Link>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -4 }}
      transition={{ duration: 0.3 }}
    >
      <Link to={`/djs/${dj.id}`} className="block group h-full" data-testid={`dj-card-${dj.id}`}>
        <div className="bg-surface/80 backdrop-blur-md border border-white/10 overflow-hidden hover:border-neon-red/40 transition-all h-full flex flex-col">
          {/* Photo */}
          <div className="relative h-56 overflow-hidden">
            {photoUrl ? (
              <img src={photoUrl} alt={dj.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-surface-highlight to-black flex items-center justify-center">
                <Mic2 className="w-16 h-16 text-chrome-dark" />
              </div>
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
            {dj.show_name && (
              <div className="absolute bottom-3 left-3 right-3">
                <p className="font-mono text-[10px] tracking-widest text-neon-red">{dj.show_name}</p>
              </div>
            )}
          </div>

          {/* Info */}
          <div className="p-5 flex-1 flex flex-col">
            <h3 className="font-display text-xl chrome-text mb-0.5 group-hover:text-neon-red transition-colors">
              {dj.stage_name || dj.name}
            </h3>
            {dj.stage_name && dj.stage_name !== dj.name && (
              <p className="text-chrome-dark text-xs mb-2">{dj.name}</p>
            )}
            <p className="text-chrome-mid text-sm leading-relaxed line-clamp-2 mb-3 flex-1">{dj.bio}</p>
            
            {dj.show_schedule && (
              <div className="flex items-center gap-1.5 text-chrome-dark text-xs mb-3">
                <Calendar className="w-3 h-3" />
                <span>{dj.show_schedule}</span>
              </div>
            )}

            <div className="flex flex-wrap gap-1.5">
              {(dj.genres || []).slice(0, 3).map(g => (
                <span key={g} className={`text-[10px] px-2 py-0.5 border ${genreColors[g] || defaultGenreColor}`}>{g}</span>
              ))}
              {(dj.genres || []).length > 3 && (
                <span className="text-[10px] px-2 py-0.5 text-chrome-dark">+{dj.genres.length - 3}</span>
              )}
            </div>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

// DJ Detail Page
const DJDetailPage = () => {
  const { djId } = useParams();
  const navigate = useNavigate();
  const [dj, setDj] = useState(null);
  const [gallery, setGallery] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => {
    const fetchDJ = async () => {
      try {
        const [djRes, galleryRes] = await Promise.all([
          axios.get(`${API}/djs/${djId}`),
          axios.get(`${API}/djs/${djId}/gallery`)
        ]);
        setDj(djRes.data);
        setGallery(galleryRes.data);
      } catch (e) {
        console.error("DJ not found");
      } finally {
        setLoading(false);
      }
    };
    fetchDJ();
  }, [djId]);

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-neon-red border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!dj) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center text-center" data-testid="dj-not-found">
        <div>
          <Mic2 className="w-16 h-16 text-chrome-dark mx-auto mb-4" />
          <p className="text-chrome-mid text-lg mb-4">DJ not found</p>
          <Link to="/djs" className="text-neon-red hover:underline">Back to All DJs</Link>
        </div>
      </div>
    );
  }

  const photoUrl = dj.photo_url 
    ? (dj.photo_url.startsWith("/api/") ? `${BACKEND_URL}${dj.photo_url}` : dj.photo_url) 
    : null;

  const socials = [
    { key: "instagram", url: dj.instagram, label: "Instagram" },
    { key: "twitter", url: dj.twitter, label: "Twitter / X" },
    { key: "facebook", url: dj.facebook, label: "Facebook" },
    { key: "soundcloud", url: dj.soundcloud, label: "SoundCloud" },
    { key: "website", url: dj.website, label: "Website" },
  ].filter(s => s.url);

  return (
    <main className="min-h-screen" data-testid={`dj-detail-${dj.id}`}>
      {/* Hero */}
      <div className="relative h-[50vh] min-h-[400px]">
        {photoUrl ? (
          <img src={photoUrl} alt={dj.name} className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-neon-red/10 to-black" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/70 to-black/30" />
        
        <div className="absolute top-24 left-6">
          <button onClick={() => navigate("/djs")} className="flex items-center gap-2 text-chrome-mid hover:text-white transition-colors bg-black/40 backdrop-blur-sm px-4 py-2" data-testid="back-to-djs">
            <ArrowLeft className="w-4 h-4" /> All DJs
          </button>
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12">
          <div className="max-w-5xl mx-auto">
            {dj.is_featured && (
              <div className="inline-flex items-center gap-1.5 bg-neon-red px-3 py-1 mb-4">
                <Star className="w-3 h-3 fill-white" />
                <span className="font-mono text-xs tracking-wider text-white">FEATURED DJ</span>
              </div>
            )}
            {dj.show_name && (
              <p className="font-mono text-xs tracking-[0.3em] text-neon-red mb-2">{dj.show_name}</p>
            )}
            <h1 className="font-display text-4xl md:text-6xl text-white mb-2">{dj.stage_name || dj.name}</h1>
            {dj.stage_name && dj.stage_name !== dj.name && (
              <p className="text-chrome-mid text-lg">{dj.name}</p>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-6 py-12">
        <div className="grid md:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="md:col-span-2 space-y-8">
            <div>
              <h2 className="font-display text-xl chrome-text mb-4">ABOUT</h2>
              <p className="text-chrome-mid leading-relaxed text-lg">{dj.bio}</p>
            </div>

            {/* Genres */}
            <div>
              <h2 className="font-display text-xl chrome-text mb-4">SPECIALTY GENRES</h2>
              <div className="flex flex-wrap gap-2">
                {(dj.genres || []).map(g => (
                  <span key={g} className={`text-sm px-4 py-2 border ${genreColors[g] || defaultGenreColor}`}>
                    <Music className="w-3 h-3 inline mr-1.5" />{g}
                  </span>
                ))}
              </div>
            </div>

            {/* Photo Gallery */}
            {gallery.length > 0 && (
              <div>
                <h2 className="font-display text-xl chrome-text mb-4 flex items-center gap-2">
                  <Camera className="w-5 h-5 text-neon-red" /> GALLERY
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="dj-gallery">
                  {gallery.map((photo, i) => {
                    const imgUrl = photo.photo_url.startsWith("/api/") ? `${BACKEND_URL}${photo.photo_url}` : photo.photo_url;
                    return (
                      <motion.div
                        key={photo.id}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: i * 0.05 }}
                        className="relative group cursor-pointer aspect-square overflow-hidden bg-surface-highlight"
                        onClick={() => setLightboxIndex(i)}
                        data-testid={`gallery-photo-${i}`}
                      >
                        <img src={imgUrl} alt={photo.caption || `Performance photo ${i + 1}`} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-end">
                          {photo.caption && (
                            <p className="text-white text-xs p-2 opacity-0 group-hover:opacity-100 transition-opacity">{photo.caption}</p>
                          )}
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Schedule Card */}
            {dj.show_schedule && (
              <div className="bg-surface/80 border border-white/10 p-6">
                <h3 className="font-mono text-xs tracking-widest text-neon-red mb-3">ON AIR</h3>
                {dj.show_name && <p className="text-white font-medium mb-1">{dj.show_name}</p>}
                <div className="flex items-center gap-2 text-chrome-mid">
                  <Calendar className="w-4 h-4" />
                  <span>{dj.show_schedule}</span>
                </div>
                <Link to="/stream" className="inline-flex items-center gap-2 mt-4 text-neon-red text-sm hover:underline" data-testid="listen-now-link">
                  <Radio className="w-4 h-4" /> Listen Now
                </Link>
              </div>
            )}

            {/* Social Links */}
            {socials.length > 0 && (
              <div className="bg-surface/80 border border-white/10 p-6">
                <h3 className="font-mono text-xs tracking-widest text-neon-red mb-3">CONNECT</h3>
                <div className="space-y-3">
                  {socials.map(s => {
                    const href = s.url.startsWith("http") ? s.url 
                      : s.key === "instagram" ? `https://instagram.com/${s.url.replace("@","")}`
                      : s.key === "twitter" ? `https://twitter.com/${s.url.replace("@","")}`
                      : s.url;
                    return (
                      <a key={s.key} href={href} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-3 text-chrome-mid hover:text-white transition-colors group"
                        data-testid={`social-${s.key}`}
                      >
                        <SocialIcon type={s.key} />
                        <span className="text-sm">{s.url}</span>
                        <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity ml-auto" />
                      </a>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Request Song */}
            <Link to="/request" className="block bg-neon-red/10 border border-neon-red/30 p-6 hover:bg-neon-red/20 transition-colors group" data-testid="request-song-link">
              <p className="text-neon-red font-medium mb-1 group-hover:text-white transition-colors">Request a Song</p>
              <p className="text-chrome-mid text-sm">Want to hear something specific? Send a request!</p>
            </Link>
          </div>
        </div>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {lightboxIndex !== null && gallery[lightboxIndex] && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[200] bg-black/95 flex items-center justify-center"
            onClick={() => setLightboxIndex(null)}
            data-testid="lightbox"
          >
            <button
              onClick={(e) => { e.stopPropagation(); setLightboxIndex(null); }}
              className="absolute top-6 right-6 p-2 text-white hover:text-neon-red z-10"
            >
              <X className="w-8 h-8" />
            </button>
            
            {lightboxIndex > 0 && (
              <button
                onClick={(e) => { e.stopPropagation(); setLightboxIndex(lightboxIndex - 1); }}
                className="absolute left-4 top-1/2 -translate-y-1/2 p-3 text-white hover:text-neon-red bg-black/50 backdrop-blur-sm z-10"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
            )}
            
            {lightboxIndex < gallery.length - 1 && (
              <button
                onClick={(e) => { e.stopPropagation(); setLightboxIndex(lightboxIndex + 1); }}
                className="absolute right-4 top-1/2 -translate-y-1/2 p-3 text-white hover:text-neon-red bg-black/50 backdrop-blur-sm z-10"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            )}

            <motion.div
              key={lightboxIndex}
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="max-w-5xl max-h-[85vh] mx-4"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={(() => {
                  const p = gallery[lightboxIndex].photo_url;
                  return p.startsWith("/api/") ? `${BACKEND_URL}${p}` : p;
                })()}
                alt={gallery[lightboxIndex].caption || "Gallery photo"}
                className="max-w-full max-h-[80vh] object-contain mx-auto"
              />
              {gallery[lightboxIndex].caption && (
                <p className="text-center text-chrome-mid text-sm mt-4">{gallery[lightboxIndex].caption}</p>
              )}
              <p className="text-center text-chrome-dark text-xs mt-2">{lightboxIndex + 1} / {gallery.length}</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
};

// Main DJs Page
const DJsPage = () => {
  const [djs, setDjs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDJs = async () => {
      try {
        const res = await axios.get(`${API}/djs`);
        setDjs(res.data);
      } catch (e) {
        console.error("Failed to fetch DJs");
      } finally {
        setLoading(false);
      }
    };
    fetchDJs();
  }, []);

  const featuredDJ = djs.find(d => d.is_featured);
  const otherDJs = djs.filter(d => !d.is_featured);

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="text-center">
          <motion.div animate={{ rotate: 360 }} transition={{ duration: 2, repeat: Infinity, ease: "linear" }}>
            <Mic2 className="w-12 h-12 text-neon-red mx-auto" />
          </motion.div>
          <p className="font-mono text-sm text-chrome-mid mt-4">LOADING DJS...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 pb-24 px-4 md:px-6" data-testid="djs-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="flex items-center gap-4 mb-10">
          <div className="red-line h-12" />
          <div>
            <p className="font-mono text-xs tracking-[0.3em] text-neon-red mb-1">THE CREW</p>
            <h1 className="font-display text-3xl md:text-5xl chrome-text">OUR DJs</h1>
          </div>
        </motion.div>

        {djs.length === 0 ? (
          <div className="text-center py-20">
            <Mic2 className="w-16 h-16 text-chrome-dark mx-auto mb-4" />
            <p className="text-chrome-mid text-lg mb-2">No DJs yet</p>
            <p className="text-chrome-dark">Check back soon — our lineup is being assembled!</p>
          </div>
        ) : (
          <>
            {/* Featured DJ */}
            {featuredDJ && (
              <div className="mb-10">
                <DJCard dj={featuredDJ} featured />
              </div>
            )}

            {/* DJ Grid */}
            {otherDJs.length > 0 && (
              <>
                {featuredDJ && (
                  <p className="font-mono text-xs tracking-widest text-chrome-mid mb-6">ALL DJS</p>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {otherDJs.map((dj, i) => (
                    <motion.div key={dj.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}>
                      <DJCard dj={dj} />
                    </motion.div>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export { DJsPage, DJDetailPage, DJCard };
export default DJsPage;
